import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Sprite, SparkleBurst, PromptCapsule, StarPips, AmbientLayer } from "./fx";

// WaterGarden — pick up a real watering can and HOLD it over each drooping
// flower: the can tilts, a sparkling stream pours, and the flower springs up
// and blooms with a happy bee. Faithful port of iOS CityHelperGardenView.

const POUR_SECONDS = 0.9;
const PLANT_LINE = 0.62; // stage fraction where stems emerge
const FLOWER_TOP = 0.5; // flower center as stage fraction

const WaterGarden = ({ payload, sounds, onDone }) => {
  const flowers = payload.flowers;
  const total = flowers.length;
  const stageRef = useRef(null);
  const doneRef = useRef(false);

  const [bloomed, setBloomed] = useState(() => new Set());
  const [pour, setPour] = useState({});
  const [canPos, setCanPos] = useState(null);
  const [wateringId, setWateringId] = useState(null);
  const [burst, setBurst] = useState({ key: 0, x: 0, y: 0 });
  const [celebrate, setCelebrate] = useState(false);
  const [size, setSize] = useState({ w: 0, h: 0 });

  const canPosRef = useRef(null);
  const pourRef = useRef({});
  const bloomedRef = useRef(new Set());
  const celebrateRef = useRef(false);
  const rafRef = useRef(0);
  const lastTsRef = useRef(0);

  const flowerUnitX = useCallback(
    (i) => (total === 1 ? 0.5 : 0.3 + (i / (total - 1)) * 0.4),
    [total]
  );

  const measure = useCallback(() => {
    const el = stageRef.current;
    if (el) setSize({ w: el.clientWidth, h: el.clientHeight });
  }, []);

  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  const bloom = useCallback(
    (id) => {
      if (bloomedRef.current.has(id)) return;
      bloomedRef.current.add(id);
      setBloomed(new Set(bloomedRef.current));
      pourRef.current = { ...pourRef.current, [id]: 1 };
      setPour(pourRef.current);
      sounds.playPop(bloomedRef.current.size / Math.max(total, 1));
      sounds.playSparkle();
      const rect = stageRef.current?.getBoundingClientRect();
      if (rect) {
        setBurst((b) => ({
          key: b.key + 1,
          x: flowerUnitX(id) * rect.width,
          y: FLOWER_TOP * rect.height - rect.height * 0.12,
        }));
      }
      if (bloomedRef.current.size >= total && !doneRef.current) {
        doneRef.current = true;
        celebrateRef.current = true;
        setCelebrate(true);
        sounds.playPop(0.95);
        window.setTimeout(() => onDone(), 1300);
      }
    },
    [flowerUnitX, onDone, sounds, total]
  );

  useEffect(() => {
    const tick = (ts) => {
      const last = lastTsRef.current || ts;
      const dt = Math.min(0.05, (ts - last) / 1000);
      lastTsRef.current = ts;
      const rect = stageRef.current?.getBoundingClientRect();
      const c = canPosRef.current;
      if (rect && c && !celebrateRef.current) {
        const canSize = Math.min(rect.width * 0.34, 150);
        const spoutX = c.x + canSize * 0.32;
        const threshold = rect.width * 0.12;
        const plantLineY = PLANT_LINE * rect.height;
        let target = null;
        let best = Infinity;
        flowers.forEach((_, i) => {
          if (bloomedRef.current.has(i)) return;
          const fx = flowerUnitX(i) * rect.width;
          const dx = Math.abs(spoutX - fx);
          if (dx < threshold && c.y < plantLineY && dx < best) {
            best = dx;
            target = i;
          }
        });
        setWateringId(target);
        if (target != null) {
          const next = Math.min(1, (pourRef.current[target] || 0) + dt / POUR_SECONDS);
          pourRef.current = { ...pourRef.current, [target]: next };
          setPour(pourRef.current);
          if (next >= 1) bloom(target);
        }
      } else {
        setWateringId(null);
      }
      rafRef.current = window.requestAnimationFrame(tick);
    };
    rafRef.current = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(rafRef.current);
  }, [bloom, flowers, flowerUnitX]);

  const handleDown = useCallback((e) => {
    if (celebrateRef.current) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const rect = stageRef.current?.getBoundingClientRect();
    const p = { x: e.clientX - (rect?.left || 0), y: e.clientY - (rect?.top || 0) };
    canPosRef.current = p;
    lastTsRef.current = 0;
    setCanPos(p);
  }, []);

  const handleMove = useCallback((e) => {
    if (!canPosRef.current) return;
    const rect = stageRef.current?.getBoundingClientRect();
    const p = { x: e.clientX - (rect?.left || 0), y: e.clientY - (rect?.top || 0) };
    canPosRef.current = p;
    setCanPos(p);
  }, []);

  const handleUp = useCallback(() => {
    canPosRef.current = null;
    setCanPos(null);
    setWateringId(null);
  }, []);

  const canSize = Math.min(size.w * 0.34, 150) || 120;
  const restPos = { x: size.w * 0.82 || 260, y: size.h * 0.5 || 200 };
  const pos = canPos || restPos;
  const tilt = wateringId != null ? 24 : canPos ? 8 : -4;

  return (
    <div className="ch-activity-body ch-garden">
      <PromptCapsule text={payload.prompt} sounds={sounds} />
      <div style={{ display: "flex", justifyContent: "center", paddingBottom: 6 }}>
        <StarPips total={total} done={bloomed.size} />
      </div>
      <div
        className="ch-stage"
        ref={stageRef}
        onPointerMove={handleMove}
        onPointerUp={handleUp}
        onPointerCancel={handleUp}
        style={{ touchAction: "none" }}
      >
        <AmbientLayer variant="day" />
        <span className="ch-garden-sun"><Sprite name="ch_zone_sun" /></span>

        {flowers.map((f, i) => {
          const isBloomed = bloomed.has(i);
          const fill = pour[i] || 0;
          const isWatering = wateringId === i;
          const scale = isBloomed ? 1 : isWatering ? 0.9 + 0.14 * fill : 0.9;
          return (
            <div
              key={i}
              className={`ch-garden-flower ${isBloomed ? "is-bloomed" : ""}`}
              style={{
                left: `${flowerUnitX(i) * 100}%`,
                top: `${FLOWER_TOP * 100}%`,
                "--ch-flower-tint": f.tint,
              }}
            >
              {isBloomed && <span className="ch-flower-glow" />}
              <motion.div
                className="ch-flower-stack"
                animate={{ scale }}
                transition={{ type: "spring", stiffness: 220, damping: 14 }}
              >
                <Sprite name={f.wilted} className={`ch-flower-pose ${isBloomed ? "is-hidden" : ""}`} />
                <Sprite name={f.bloom} className={`ch-flower-pose ch-flower-bloom ${isBloomed ? "is-shown" : ""}`} />
              </motion.div>
            </div>
          );
        })}

        <span className="ch-garden-planter"><Sprite name="ch_garden_planter" /></span>

        {wateringId != null && canPos && (
          <div
            className="ch-water-stream"
            style={{ left: pos.x + canSize * 0.32, top: pos.y + canSize * 0.14 }}
          >
            {[0, 1, 2, 3, 4].map((i) => (
              <span key={i} className="ch-water-drop" style={{ animationDelay: `${i * 0.09}s` }} />
            ))}
          </div>
        )}

        {wateringId != null && canPos && (
          <div
            className="ch-water-splash"
            style={{
              left: `${flowerUnitX(wateringId) * 100}%`,
              top: `${(FLOWER_TOP + 0.15) * 100}%`,
            }}
            aria-hidden="true"
          >
            {[0, 1, 2, 3].map((i) => (
              <span
                key={i}
                style={{ animationDelay: `${i * 0.12}s`, "--ch-splash-x": `${(i - 1.5) * 16}px` }}
              />
            ))}
          </div>
        )}

        {celebrate &&
          flowers.map((_, i) => (
            <motion.span
              key={`bee-${i}`}
              className="ch-garden-bee"
              style={{ left: `${flowerUnitX(i) * 100}%`, top: `${FLOWER_TOP * 100 - 6}%` }}
              initial={{ y: 0, opacity: 1 }}
              animate={{ y: -170, opacity: 0, x: (i % 2 ? 24 : -24) }}
              transition={{ duration: 1.6, delay: i * 0.2, ease: "easeOut" }}
            >
              <Sprite name="ch_bee" />
            </motion.span>
          ))}

        <img
          src="/assets/games/city-helpers/ch_wateringcan.png"
          alt="Watering can"
          draggable="false"
          className="ch-watering-can"
          style={{
            left: pos.x,
            top: pos.y,
            width: canSize,
            height: canSize,
            transform: `translate(-50%, -50%) rotate(${tilt}deg) scale(${canPos ? 1.05 : 1})`,
          }}
          onPointerDown={handleDown}
        />

        <SparkleBurst trigger={burst.key} reach={95} style={{ left: burst.x, top: burst.y }} />
      </div>
    </div>
  );
};

export default WaterGarden;
